import React, { useState, useMemo, useEffect } from 'react';
import {
  Student,
  Teacher,
  SchoolSettings,
  AttendanceRecord,
  HomeroomJournalNote,
} from '../../types';
import {
  FileText,
  Sparkles,
  RefreshCw,
  Copy,
  Check,
  Printer,
  Search,
  Award,
  Calendar,
  BookOpen,
  User,
  CheckCircle2,
  AlertCircle,
  HelpCircle,
  Save,
  Sliders,
  Filter,
  Layers,
  ChevronRight,
  Send,
  Building2,
  Trophy,
  Zap,
} from 'lucide-react';

export interface StudentAcademicProgress {
  id?: string;
  studentId: string;
  studentName?: string;
  class?: string;
  semesters: {
    semesterName: string;
    subjects: {
      subjectName: string;
      grade: number;
    }[];
  }[];
}

interface ReportCardNotesManagerProps {
  homeroomClass: string;
  students: Student[];
  academicProgress: StudentAcademicProgress[];
  attendanceRecords: AttendanceRecord[];
  homeroomNotes: HomeroomJournalNote[];
  activeUser: Teacher;
  schoolSettings: SchoolSettings;
  appTheme?: string;
}

export const ReportCardNotesManager: React.FC<ReportCardNotesManagerProps> = ({
  homeroomClass,
  students,
  academicProgress,
  attendanceRecords,
  homeroomNotes,
  activeUser,
  schoolSettings,
}) => {
  // Filter students for active homeroom class
  const classStudents = useMemo(() => {
    return students.filter((s) => s.class === homeroomClass && s.status === 'Aktif');
  }, [students, homeroomClass]);

  const [selectedStudentId, setSelectedStudentId] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedSemester, setSelectedSemester] = useState<string>('Akumulasi Semua Semester');
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [savedSuccessId, setSavedSuccessId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'individual' | 'class_table'>('individual');

  // Variation Index per student ID: 0, 1, 2, 3
  const [variationMap, setVariationMap] = useState<Record<string, number>>({});
  
  // Custom edited notes per student ID
  const [notesMap, setNotesMap] = useState<Record<string, string>>({});

  // Local storage persistence key
  const storageKey = `report_card_notes_${homeroomClass}_${schoolSettings?.academicYear || 'current'}`;

  // Load saved notes from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.notes) setNotesMap(parsed.notes);
        if (parsed.variations) setVariationMap(parsed.variations);
      }
    } catch (e) {
      console.error('Failed to load saved report card notes:', e);
    }
  }, [storageKey]);

  // Save notes to localStorage
  const saveToStorage = (updatedNotes: Record<string, string>, updatedVariations?: Record<string, number>) => {
    try {
      localStorage.setItem(
        storageKey,
        JSON.stringify({
          notes: updatedNotes,
          variations: updatedVariations || variationMap,
          updatedAt: new Date().toISOString(),
        })
      );
    } catch (e) {
      console.error('Failed to save report card notes:', e);
    }
  };

  // Set default selected student
  useEffect(() => {
    if (classStudents.length > 0 && !selectedStudentId) {
      setSelectedStudentId(classStudents[0].id);
    }
  }, [classStudents, selectedStudentId]);

  // 1. Calculate Rankings & Analytics for All Students in Class
  const classAnalytics = useMemo(() => {
    const analytics: Record<
      string,
      {
        overallAvg: number;
        totalGradeSum: number;
        semesterAvgs: Record<string, number>;
        topSubjects: string[];
        lowSubjects: string[];
        sakit: number;
        izin: number;
        alpa: number;
        totalRecordedDays: number;
        attendancePercentage: number;
        positiveNotesCount: number;
        improvementNotesCount: number;
        journalSummary: string[];
      }
    > = {};

    // First pass: Academic & Attendance
    classStudents.forEach((student) => {
      // Academic Progress Data
      const stProg = academicProgress.find((p) => p.studentId === student.id);
      const semAvgs: Record<string, number> = {};
      let totalGradeSum = 0;
      let totalSubjectCount = 0;
      const allSubjectGrades: { name: string; grade: number }[] = [];

      if (stProg && stProg.semesters) {
        stProg.semesters.forEach((sem) => {
          if (sem.subjects && sem.subjects.length > 0) {
            const sum = sem.subjects.reduce((a, b) => a + b.grade, 0);
            const avg = parseFloat((sum / sem.subjects.length).toFixed(1));
            semAvgs[sem.semesterName] = avg;

            sem.subjects.forEach((subj) => {
              allSubjectGrades.push({ name: subj.subjectName, grade: subj.grade });
              totalGradeSum += subj.grade;
              totalSubjectCount++;
            });
          }
        });
      }

      const overallAvg =
        totalSubjectCount > 0 ? parseFloat((totalGradeSum / totalSubjectCount).toFixed(1)) : 0;

      // Deduplicate and average subjects
      const subjectMap: Record<string, { sum: number; count: number }> = {};
      allSubjectGrades.forEach((item) => {
        if (!subjectMap[item.name]) subjectMap[item.name] = { sum: 0, count: 0 };
        subjectMap[item.name].sum += item.grade;
        subjectMap[item.name].count += 1;
      });

      const averagedSubjects = Object.keys(subjectMap).map((subj) => ({
        name: subj,
        avg: parseFloat((subjectMap[subj].sum / subjectMap[subj].count).toFixed(1)),
      }));

      averagedSubjects.sort((a, b) => b.avg - a.avg);
      const topSubjects = averagedSubjects.slice(0, 2).map((s) => s.name);
      const lowSubjects = averagedSubjects.slice(-2).reverse().map((s) => s.name);

      // Attendance Data
      const studentAttendances = attendanceRecords.filter((a) => a.studentId === student.id);
      let sakit = 0;
      let izin = 0;
      let alpa = 0;
      let hadir = 0;

      studentAttendances.forEach((att) => {
        if (att.status === 'Sakit') sakit++;
        else if (att.status === 'Izin') izin++;
        else if (att.status === 'Alpa') alpa++;
        else if (att.status === 'Hadir') hadir++;
      });

      const totalRecordedDays = studentAttendances.length;
      const totalAbsence = sakit + izin + alpa;
      const attendancePercentage =
        totalRecordedDays > 0
          ? Math.round(((totalRecordedDays - alpa) / totalRecordedDays) * 100)
          : 100;

      // Homeroom Journal Notes Data
      const stNotes = homeroomNotes.filter((n) => n.studentId === student.id);
      let positiveNotesCount = 0;
      let improvementNotesCount = 0;
      const journalSummary: string[] = [];

      stNotes.forEach((n) => {
        if (['Prestasi', 'Karakter', 'Akademik'].includes(n.category)) {
          positiveNotesCount++;
          if (n.description) journalSummary.push(n.description);
        } else if (['Pelanggaran', 'Kedisiplinan', 'Kehadiran'].includes(n.category)) {
          improvementNotesCount++;
        }
      });

      analytics[student.id] = {
        overallAvg,
        totalGradeSum,
        semesterAvgs: semAvgs,
        topSubjects,
        lowSubjects,
        sakit,
        izin,
        alpa,
        totalRecordedDays,
        attendancePercentage,
        positiveNotesCount,
        improvementNotesCount,
        journalSummary,
      };
    });

    return analytics;
  }, [classStudents, academicProgress, attendanceRecords, homeroomNotes]);

  // Overall Rankings based on Total Sum of all subject grades
  const rankings = useMemo(() => {
    const sorted = [...classStudents]
      .map((s) => ({
        id: s.id,
        totalSum: classAnalytics[s.id]?.totalGradeSum || 0,
        avg: classAnalytics[s.id]?.overallAvg || 0,
      }))
      .sort((a, b) => {
        if (b.totalSum !== a.totalSum) return b.totalSum - a.totalSum;
        return b.avg - a.avg;
      });

    const rankMap: Record<string, { rank: number; total: number; totalSum: number }> = {};
    sorted.forEach((item, index) => {
      rankMap[item.id] = { rank: index + 1, total: sorted.length, totalSum: item.totalSum };
    });
    return rankMap;
  }, [classStudents, classAnalytics]);

  // 2. Generate Sentence Variations Function
  const generateSentenceVariations = (student: Student, variationIndex: number = 0) => {
    const data = classAnalytics[student.id];
    const rankInfo = rankings[student.id];

    if (!data) return `Ananda ${student.name} telah mengikuti kegiatan pembelajaran dengan baik di Kelas ${homeroomClass}.`;

    const name = student.name;
    const firstName = name.split(' ')[0];
    const avg = data.overallAvg;
    const rankText = rankInfo ? `Peringkat #${rankInfo.rank} dari ${rankInfo.total} siswa` : '';
    const topSubjText =
      data.topSubjects.length > 0 ? data.topSubjects.join(' dan ') : 'mata pelajaran umum';
    const lowSubjText =
      data.lowSubjects.length > 0 ? data.lowSubjects.join(' & ') : 'seluruh mata pelajaran';

    const sakit = data.sakit;
    const izin = data.izin;
    const alpa = data.alpa;

    let presenceSentence = '';
    if (alpa === 0 && sakit === 0 && izin === 0) {
      presenceSentence = 'Tingkat kehadiran ananda sangat sempurna (100% hadir tanpa absen).';
    } else if (alpa === 0) {
      presenceSentence = `Kehadiran tergolong sangat disiplin dengan catatan ${sakit} sakit, ${izin} izin, dan 0 alpa.`;
    } else {
      presenceSentence = `Kehadiran mencatat ${sakit} sakit, ${izin} izin, dan ${alpa} alpa. Diharapkan ke depan lebih disiplin hadir tepat waktu.`;
    }

    const journalAddon =
      data.positiveNotesCount > 0
        ? ` Ananda juga menunjukkan sikap positif dan keaktifan yang baik dalam jurnal perwalian kelas.`
        : '';

    // 10 Distinct Qualitative Sentence Tone Styles
    switch (variationIndex % 10) {
      case 0:
        // Variasi 1: Formal & Prestasi Akademik
        return `Ananda ${name} menunjukkan capaian akademik yang sangat baik dan konsisten di Kelas ${homeroomClass}. Ananda memiliki minat dan pemahaman yang kuat, khususnya pada mata pelajaran ${topSubjText}. Kehadiran dan ketertiban ananda di kelas tergolong sangat disiplin.${journalAddon} Diharapkan ${firstName} terus mempertahankan konsistensi dan semangat belajar ini pada semester berikutnya.`;

      case 1:
        // Variasi 2: Motivatif & Pembinaan Karakter
        return `Selamat untuk Ananda ${name} atas perjuangan dan ketekunan belajar di Kelas ${homeroomClass}! Ananda memperlihatkan potensi akademik serta partisipasi yang aktif, terutama pada ${topSubjText}. Sikap sopan santun dan kedisiplinan kehadiran ananda di kelas patut diacungi jempol. Pertahankan semangat positif dan tetap optimis meraih prestasi tinggi.`;

      case 2:
        // Variasi 3: Apresiatif & Komunikatif Orang Tua
        return `Catatan Orang Tua/Wali: Ananda ${name} telah menyelesaikan proses belajar dengan hasil yang membanggakan. Kedisiplinan, sopan santun, serta tingkat kehadiran ananda di sekolah sangat patut diapresiasi. Terima kasih atas bimbingan Bapak/Ibu di rumah, mari bersama terus mendampingi ${firstName} agar semakin sukses.`;

      case 3:
        // Variasi 4: Bimbingan & Fokus Perbaikan Suportif
        return `Ananda ${name} telah mengikuti seluruh rangkaian kegiatan pembelajaran di Kelas ${homeroomClass} dengan baik. Untuk lebih mengoptimalkan capaian belajar, ananda perlu memberikan perhatian lebih pada pemahaman ${lowSubjText}. Diharapkan ${firstName} semakin rajin bertanya, aktif berdiskusi, serta konsisten mengatur waktu belajar.`;

      case 4:
        // Variasi 5: Kepemimpinan & Sosial (Soft Skills)
        return `Ananda ${name} menunjukkan jiwa kepemimpinan, kepedulian sosial, dan kemampuan kerja sama yang sangat baik dalam kegiatan kelompok di Kelas ${homeroomClass}. Ananda ramah, bertanggung jawab terhadap tugas kelas, dan mudah beradaptasi dengan lingkungan sekitar. Pertahankan karakter positif ini sebagai teladan bagi teman sebaya.`;

      case 5:
        // Variasi 6: Seni, Kreativitas & Ekstrakurikuler
        return `Selain prestasi di bidang akademis, Ananda ${name} juga aktif menunjukkan kreativitas, bakat, serta antusiasme yang tinggi dalam kegiatan seni dan pengembangan diri di sekolah. Partisipasi aktif ${firstName} patut diapresiasi dan terus dikembangkan untuk mengasah potensinya secara maksimal.`;

      case 6:
        // Variasi 7: Kemandirian & Tanggung Jawab
        return `Ananda ${name} merupakan siswa yang mandiri, disiplin, dan memiliki tanggung jawab tinggi dalam menyelesaikan setiap tugas yang diberikan di Kelas ${homeroomClass}. Ketepatan waktu dalam pengumpulan tugas serta kerapian catatan menunjukkan etos kerja belajar yang sangat baik. Pertahankan terus!`;

      case 7:
        // Variasi 8: Religius & Budi Pekerti Luhur
        return `Syukur Alhamdulillah, Ananda ${name} menunjukkan akhlak mulia, kepatuhan dalam beribadah, serta budi pekerti yang luhur selama berinteraksi di lingkungan sekolah. Sikap jujur, hormat kepada guru, dan santun kepada sesama teman menjadi keunggulan karakter ananda.`;

      case 8:
        // Variasi 9: Kesehatan & Kebugaran Jasmani
        return `Ananda ${name} selalu tampil bugar, energik, dan memiliki semangat tinggi dalam mengikuti kegiatan pembelajaran serta aktivitas fisik dan olahraga di sekolah. Kesehatan dan pola hidup aktif ini mendukung produktivitas belajar ananda sepanjang semester.`;

      case 9:
        // Variasi 10: Reflektif & Perencanaan Masa Depan
        return `Ananda ${name} memiliki daya refleksi yang baik terhadap perkembangan belajarnya. Dengan pencapaian rata-rata ${avg} di Kelas ${homeroomClass}, ananda didorong untuk terus memetakan cita-cita, memperluas wawasan bacaan, dan mempersiapkan diri menghadapi tantangan belajar di tingkat selanjutnya.`;

      default:
        return `Ananda ${name} telah menyelesaikan pembelajaran di Kelas ${homeroomClass} dengan baik.`;
    }
  };

  // Get effective note sentence for a student
  const getStudentNoteSentence = (student: Student) => {
    if (notesMap[student.id]) {
      return notesMap[student.id];
    }
    const currentVar = variationMap[student.id] || 0;
    return generateSentenceVariations(student, currentVar);
  };

  // Handle "Rubah Kalimat" Button Click
  const handleRotateVariation = (studentId: string) => {
    const currentVar = variationMap[studentId] || 0;
    const nextVar = (currentVar + 1) % 10;

    const student = classStudents.find((s) => s.id === studentId);
    if (!student) return;

    const newSentence = generateSentenceVariations(student, nextVar);

    const updatedVariations = { ...variationMap, [studentId]: nextVar };
    const updatedNotes = { ...notesMap, [studentId]: newSentence };

    setVariationMap(updatedVariations);
    setNotesMap(updatedNotes);
    saveToStorage(updatedNotes, updatedVariations);
  };

  // Handle Manual Editing of Note
  const handleNoteChange = (studentId: string, newText: string) => {
    const updatedNotes = { ...notesMap, [studentId]: newText };
    setNotesMap(updatedNotes);
    saveToStorage(updatedNotes);
  };

  // Save specific note confirmation toast
  const handleSaveNote = (studentId: string) => {
    saveToStorage(notesMap);
    setSavedSuccessId(studentId);
    setTimeout(() => setSavedSuccessId(null), 2000);
  };

  // Copy to Clipboard
  const handleCopyNote = (studentId: string, text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(studentId);
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Generate for All Students at Once
  const handleGenerateAll = () => {
    const newNotes: Record<string, string> = {};
    const newVars: Record<string, number> = {};

    classStudents.forEach((student) => {
      const v = variationMap[student.id] || 0;
      newVars[student.id] = v;
      newNotes[student.id] = generateSentenceVariations(student, v);
    });

    setNotesMap(newNotes);
    setVariationMap(newVars);
    saveToStorage(newNotes, newVars);
  };

  // Print Report Card Summary Sheet
  const handlePrintReport = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const rowsHtml = classStudents
      .map((student, index) => {
        const analytics = classAnalytics[student.id];
        const rank = rankings[student.id];
        const note = getStudentNoteSentence(student);

        return `
        <tr>
          <td style="text-align: center; font-weight: bold; padding: 8px; border: 1px solid #cbd5e1;">${index + 1}</td>
          <td style="padding: 8px; border: 1px solid #cbd5e1; font-weight: bold;">${student.name}<br/><span style="font-size:10px; color:#64748b; font-weight:normal;">NIS: ${student.nis || '-'}</span></td>
          <td style="text-align: center; padding: 8px; border: 1px solid #cbd5e1; font-weight: bold; color: #4338ca;">${analytics?.totalGradeSum || '-'}</td>
          <td style="text-align: center; padding: 8px; border: 1px solid #cbd5e1; font-weight: bold;">${analytics?.overallAvg || '-'}</td>
          <td style="text-align: center; padding: 8px; border: 1px solid #cbd5e1; font-size:11px;"><strong>#${rank?.rank || '-'}</strong><br/><span style="font-size:10px; color:#64748b;">(${rank?.total || '-'} siswa)</span></td>
          <td style="text-align: center; padding: 8px; border: 1px solid #cbd5e1; font-size: 11px;">
            S: ${analytics?.sakit || 0} | I: ${analytics?.izin || 0} | A: ${analytics?.alpa || 0}
          </td>
          <td style="padding: 10px; border: 1px solid #cbd5e1; font-size: 11px; line-height: 1.5; text-align: justify;">${note}</td>
        </tr>
      `;
      })
      .join('');

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Rekap Catatan Wali Kelas di Rapor - Kelas ${homeroomClass}</title>
          <style>
            body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; padding: 20px; color: #1e293b; font-size: 12px; }
            .header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid #0f172a; padding-bottom: 12px; }
            .header h2 { margin: 0; font-size: 18px; text-transform: uppercase; letter-spacing: 1px; color: #0f172a; }
            .header p { margin: 4px 0 0 0; font-size: 12px; color: #475569; }
            table { width: 100%; border-collapse: collapse; margin-top: 15px; }
            th { background-color: #f1f5f9; padding: 10px; border: 1px solid #94a3b8; font-size: 11px; text-transform: uppercase; font-weight: 800; }
            .footer { margin-top: 30px; display: flex; justify-content: space-between; font-size: 11px; }
            .sign-box { text-align: center; width: 220px; }
          </style>
        </head>
        <body>
          <div class="header">
            <h2>${schoolSettings?.schoolName || 'SMP NEGERI / SEKOH TINGKAT PERTAMA'}</h2>
            <p><strong>REKAPITULASI CATATAN WALI KELAS DI RAPOR SISWA</strong></p>
            <p>Kelas: <strong>${homeroomClass}</strong> | Tahun Ajaran: <strong>${schoolSettings?.academicYear || '2025/2026'}</strong> | Wali Kelas: <strong>${activeUser.name}</strong></p>
          </div>

          <table>
            <thead>
              <tr>
                <th style="width: 35px;">No</th>
                <th style="width: 150px;">Nama Siswa</th>
                <th style="width: 60px;">Jml Nilai</th>
                <th style="width: 60px;">Rata Rapor</th>
                <th style="width: 60px;">Rangking</th>
                <th style="width: 85px;">Kehadiran (S/I/A)</th>
                <th>Narasi Catatan Wali Kelas di Rapor</th>
              </tr>
            </thead>
            <tbody>
              ${rowsHtml}
            </tbody>
          </table>

          <div class="footer">
            <div class="sign-box">
              <p>Mengetahui,<br/>Kepala Sekolah</p>
              <br/><br/><br/>
              <p><strong>${schoolSettings?.principalName || '...............................'}</strong><br/>NIP. ${schoolSettings?.principalNip || '-'}</p>
            </div>
            <div class="sign-box">
              <p>Wali Kelas ${homeroomClass},</p>
              <br/><br/><br/>
              <p><strong>${activeUser.name}</strong><br/>NIP. ${activeUser.nip || '-'}</p>
            </div>
          </div>
          <script>
            window.onload = function() { window.print(); }
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  // Filtered Students for Sidebar List
  const filteredStudents = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return classStudents;
    return classStudents.filter(
      (s) =>
        s.name.toLowerCase().includes(q) ||
        (s.nis && s.nis.toLowerCase().includes(q))
    );
  }, [classStudents, searchQuery]);

  const selectedStudent = useMemo(() => {
    return classStudents.find((s) => s.id === selectedStudentId) || classStudents[0];
  }, [classStudents, selectedStudentId]);

  const activeStudentAnalytics = selectedStudent ? classAnalytics[selectedStudent.id] : null;
  const activeStudentRank = selectedStudent ? rankings[selectedStudent.id] : null;
  const activeNoteSentence = selectedStudent ? getStudentNoteSentence(selectedStudent) : '';

  return (
    <div className="space-y-6">
      {/* Top Banner Card */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-emerald-600 via-teal-600 to-indigo-700 p-6 text-white shadow-xl">
        <div className="absolute top-0 right-0 -mt-10 -mr-10 h-48 w-48 rounded-full bg-white/10 blur-2xl pointer-events-none" />
        <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="space-y-1.5">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/20 backdrop-blur-md text-xs font-black tracking-wider uppercase border border-white/20">
              <Sparkles size={14} className="text-amber-300" />
              <span>Otomatisasi Catatan Rapor Wali Kelas</span>
            </div>
            <h2 className="text-2xl font-black tracking-tight">
              Catatan Rapor Wali Kelas - Kelas {homeroomClass}
            </h2>
            <p className="text-xs text-emerald-100/90 max-w-2xl font-medium">
              Sistem secara otomatis merangkum data nilai akademik semua semester, akumulasi kehadiran (S/I/A), dan catatan perwalian menjadi narasi kalimat rapor yang santun, presisi, dan siap dipakai di e-Rapor.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2 shrink-0">
            <button
              onClick={handleGenerateAll}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-amber-400 hover:bg-amber-300 text-slate-950 font-black rounded-xl text-xs shadow-lg transition-all transform hover:scale-102 cursor-pointer"
            >
              <Zap size={16} />
              <span>Generasi Semua Siswa</span>
            </button>

            <button
              onClick={handlePrintReport}
              disabled={classStudents.length === 0}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-white/15 hover:bg-white/25 text-white font-bold rounded-xl text-xs backdrop-blur-md border border-white/20 shadow-md transition-all cursor-pointer disabled:opacity-50"
            >
              <Printer size={16} />
              <span>Cetak Rekap Kelas</span>
            </button>

            <div className="bg-black/20 p-1 rounded-xl flex items-center border border-white/20">
              <button
                onClick={() => setViewMode('individual')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                  viewMode === 'individual'
                    ? 'bg-white text-emerald-950 shadow-md'
                    : 'text-white hover:text-emerald-100'
                }`}
              >
                Detail Siswa
              </button>
              <button
                onClick={() => setViewMode('class_table')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                  viewMode === 'class_table'
                    ? 'bg-white text-emerald-950 shadow-md'
                    : 'text-white hover:text-emerald-100'
                }`}
              >
                Tabel Rekap Kelas ({classStudents.length})
              </button>
            </div>
          </div>
        </div>
      </div>

      {classStudents.length === 0 ? (
        <div className="bg-white dark:bg-slate-800 rounded-2xl p-12 text-center border border-slate-200 dark:border-slate-700/80 shadow-sm">
          <Building2 size={48} className="mx-auto text-slate-400 mb-3 opacity-60" />
          <h3 className="text-lg font-bold text-slate-800 dark:text-white">
            Belum Ada Siswa di Kelas {homeroomClass}
          </h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 max-w-sm mx-auto mt-1">
            Silakan masukkan data siswa perwalian di menu "Database Utama Peserta Didik" terlebih dahulu.
          </p>
        </div>
      ) : viewMode === 'individual' ? (
        /* MODE 1: DETAIL PER SISWA (SIDEBAR + CONTENT) */
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* LEFT SIDEBAR: STUDENT LIST */}
          <div className="lg:col-span-4 bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700/80 p-4 shadow-sm flex flex-col h-[700px]">
            <div className="mb-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-black uppercase tracking-wider text-slate-700 dark:text-slate-300 flex items-center gap-1.5">
                  <User size={15} className="text-emerald-500" />
                  <span>Daftar Siswa Perwalian</span>
                </span>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-950 text-emerald-800 dark:text-emerald-300">
                  {classStudents.length} Siswa
                </span>
              </div>

              <div className="relative">
                <Search size={14} className="absolute left-3 top-3 text-slate-400" />
                <input
                  type="text"
                  placeholder="Cari nama atau NIS..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-xs outline-none focus:ring-2 focus:ring-emerald-500 text-slate-800 dark:text-slate-200"
                />
              </div>
            </div>

            {/* Scrollable Student List */}
            <div className="flex-1 overflow-y-auto space-y-1.5 pr-1 custom-scrollbar">
              {filteredStudents.map((student, idx) => {
                const analytics = classAnalytics[student.id];
                const isSelected = student.id === selectedStudent?.id;
                const isCustomEdited = !!notesMap[student.id];

                return (
                  <button
                    key={student.id}
                    onClick={() => setSelectedStudentId(student.id)}
                    className={`w-full text-left p-3 rounded-xl border transition-all flex items-center justify-between gap-2 cursor-pointer ${
                      isSelected
                        ? 'bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-950/60 dark:to-teal-950/60 border-emerald-500 dark:border-emerald-500 shadow-xs'
                        : 'bg-white dark:bg-slate-800/60 border-slate-100 dark:border-slate-700/60 hover:bg-slate-50 dark:hover:bg-slate-700/50'
                    }`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <span className="text-[10px] font-black text-slate-400 w-4 shrink-0">
                          {idx + 1}.
                        </span>
                        <h4 className="text-xs font-bold text-slate-800 dark:text-white truncate">
                          {student.name}
                        </h4>
                      </div>
                      <div className="flex items-center gap-2 text-[10px] text-slate-500 dark:text-slate-400 mt-0.5 ml-5">
                        <span>NIS: {student.nis || '-'}</span>
                        <span>•</span>
                        <span className="font-semibold text-indigo-600 dark:text-indigo-400">
                          Rata: {analytics?.overallAvg || '-'}
                        </span>
                      </div>
                    </div>

                    <div className="flex flex-col items-end shrink-0 gap-1">
                      {isCustomEdited ? (
                        <span className="px-1.5 py-0.5 bg-amber-100 dark:bg-amber-950/80 text-amber-800 dark:text-amber-300 text-[9px] font-black rounded-md">
                          Edit
                        </span>
                      ) : (
                        <span className="px-1.5 py-0.5 bg-emerald-100 dark:bg-emerald-950/80 text-emerald-800 dark:text-emerald-300 text-[9px] font-bold rounded-md">
                          Auto
                        </span>
                      )}
                      <ChevronRight
                        size={14}
                        className={isSelected ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-300'}
                      />
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* RIGHT CONTENT: GENERATOR & DATA SUMMARY */}
          {selectedStudent && (
            <div className="lg:col-span-8 space-y-5">
              {/* STUDENT HEADER CARD */}
              <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-5 shadow-sm">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 dark:border-slate-700/80 pb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-emerald-500 to-teal-500 text-white font-black flex items-center justify-center text-lg shadow-md shrink-0">
                      {selectedStudent.name.charAt(0)}
                    </div>
                    <div>
                      <h3 className="text-base font-black text-slate-800 dark:text-white">
                        {selectedStudent.name}
                      </h3>
                      <p className="text-xs text-slate-500 dark:text-slate-400 font-medium flex items-center gap-2 mt-0.5">
                        <span>NIS: {selectedStudent.nis || '-'}</span>
                        <span>•</span>
                        <span>Gender: {selectedStudent.gender}</span>
                        <span>•</span>
                        <span className="font-bold text-emerald-600 dark:text-emerald-400">
                          Kelas {homeroomClass}
                        </span>
                      </p>
                    </div>
                  </div>

                  {activeStudentRank && (
                    <div className="px-3.5 py-1.5 bg-amber-500/10 border border-amber-500/30 text-amber-700 dark:text-amber-300 rounded-xl text-xs font-black flex items-center gap-2 shrink-0">
                      <Trophy size={16} className="text-amber-500" />
                      <span>
                        Rangking Kelas: #{activeStudentRank.rank} / {activeStudentRank.total} Siswa
                      </span>
                    </div>
                  )}
                </div>

                {/* 3 SUMMARY PILLS: AKADEMIK, KEHADIRAN, CATATAN */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
                  {/* Akademik Pill */}
                  <div className="p-3 bg-slate-50 dark:bg-slate-900/60 rounded-xl border border-slate-200/80 dark:border-slate-700/60">
                    <div className="flex items-center justify-between text-[11px] text-slate-500 font-bold mb-1">
                      <span className="flex items-center gap-1">
                        <Award size={13} className="text-indigo-500" />
                        <span>Rata-Rata Akademik</span>
                      </span>
                      <span className="text-indigo-600 dark:text-indigo-400 font-black">
                        {activeStudentAnalytics?.overallAvg || '-'}
                      </span>
                    </div>
                    <div className="text-[10px] text-slate-600 dark:text-slate-400 truncate">
                      Unggul: <strong className="text-slate-800 dark:text-slate-200">{activeStudentAnalytics?.topSubjects.join(', ') || '-'}</strong>
                    </div>
                  </div>

                  {/* Kehadiran Pill */}
                  <div className="p-3 bg-slate-50 dark:bg-slate-900/60 rounded-xl border border-slate-200/80 dark:border-slate-700/60">
                    <div className="flex items-center justify-between text-[11px] text-slate-500 font-bold mb-1">
                      <span className="flex items-center gap-1">
                        <Calendar size={13} className="text-emerald-500" />
                        <span>Rekap Kehadiran</span>
                      </span>
                      <span className="text-emerald-600 dark:text-emerald-400 font-black">
                        {activeStudentAnalytics?.attendancePercentage || 100}%
                      </span>
                    </div>
                    <div className="text-[10px] text-slate-600 dark:text-slate-400">
                      Sakit: <strong className="text-amber-600">{activeStudentAnalytics?.sakit || 0}</strong> | Izin: <strong className="text-blue-600">{activeStudentAnalytics?.izin || 0}</strong> | Alpa: <strong className="text-rose-600">{activeStudentAnalytics?.alpa || 0}</strong>
                    </div>
                  </div>

                  {/* Jurnal Catatan Pill */}
                  <div className="p-3 bg-slate-50 dark:bg-slate-900/60 rounded-xl border border-slate-200/80 dark:border-slate-700/60">
                    <div className="flex items-center justify-between text-[11px] text-slate-500 font-bold mb-1">
                      <span className="flex items-center gap-1">
                        <BookOpen size={13} className="text-purple-500" />
                        <span>Jurnal Perwalian</span>
                      </span>
                      <span className="text-purple-600 dark:text-purple-400 font-black">
                        {activeStudentAnalytics?.positiveNotesCount || 0} Catatan
                      </span>
                    </div>
                    <div className="text-[10px] text-slate-600 dark:text-slate-400 truncate">
                      Sikap: <strong className="text-slate-800 dark:text-slate-200">{activeStudentAnalytics?.positiveNotesCount ? 'Aktif & Positif' : 'Sesuai Aturan'}</strong>
                    </div>
                  </div>
                </div>
              </div>

              {/* GENERATED REPORT CARD NOTE BOX WITH "RUBAH KALIMAT" BUTTON */}
              <div className="bg-gradient-to-br from-emerald-50/80 via-teal-50/50 to-indigo-50/50 dark:from-emerald-950/30 dark:via-teal-950/20 dark:to-indigo-950/20 rounded-2xl border border-emerald-200/80 dark:border-emerald-800/50 p-6 shadow-sm space-y-4 relative">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-emerald-200/60 dark:border-emerald-800/40 pb-3">
                  <div className="flex items-center gap-2">
                    <div className="p-2 bg-emerald-600 text-white rounded-xl shadow-xs">
                      <FileText size={18} />
                    </div>
                    <div>
                      <h4 className="text-sm font-black text-slate-900 dark:text-white uppercase tracking-wider">
                        Kalimat Catatan Wali Kelas di Rapor
                      </h4>
                      <p className="text-[11px] text-slate-500 dark:text-slate-400">
                        Otomatis tersusun dari integrasi nilai akademik, kehadiran, dan jurnal perwalian.
                      </p>
                    </div>
                  </div>

                  {/* "RUBAH KALIMAT" BUTTON */}
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => handleRotateVariation(selectedStudent.id)}
                      className="inline-flex items-center gap-2 px-3.5 py-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-slate-950 font-black rounded-xl text-xs shadow-md transition-all cursor-pointer transform active:scale-95"
                      title="Klik untuk mengganti ke gaya atau variasi susunan kalimat lainnya"
                    >
                      <RefreshCw size={14} className="animate-spin-once" />
                      <span>Rubah Kalimat</span>
                      <span className="ml-1 px-1.5 py-0.2 bg-black/20 text-slate-950 text-[10px] font-black rounded-md">
                        Gaya #{((variationMap[selectedStudent.id] || 0) % 10) + 1} / 10
                      </span>
                    </button>
                  </div>
                </div>

                {/* EDITABLE TEXTAREA */}
                <div className="space-y-2">
                  <label className="text-xs font-bold text-slate-700 dark:text-slate-300 flex items-center justify-between">
                    <span>Teks Narasi Rapor (Dapat Diedit Bebas):</span>
                    <span className="text-[10px] text-slate-400 font-normal">
                      {(notesMap[selectedStudent.id] || '').length || activeNoteSentence.length} Karakter
                    </span>
                  </label>
                  <textarea
                    rows={5}
                    value={notesMap[selectedStudent.id] !== undefined ? notesMap[selectedStudent.id] : activeNoteSentence}
                    onChange={(e) => handleNoteChange(selectedStudent.id, e.target.value)}
                    className="w-full p-4 bg-white dark:bg-slate-900 border border-emerald-300/80 dark:border-emerald-700/80 rounded-xl text-xs sm:text-sm font-medium leading-relaxed text-slate-800 dark:text-slate-100 outline-none focus:ring-2 focus:ring-emerald-500 shadow-inner resize-y"
                    placeholder="Kalimat catatan rapor..."
                  />
                </div>

                {/* VARIATION SELECTOR BADGES */}
                <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
                  <div className="flex items-center gap-1.5 overflow-x-auto py-1">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mr-1">
                      Pilihan 10 Gaya:
                    </span>
                    {[
                      { idx: 0, label: '1. Formal & Prestasi' },
                      { idx: 1, label: '2. Motivatif & Karakter' },
                      { idx: 2, label: '3. Apresiatif Ortu' },
                      { idx: 3, label: '4. Fokus Perbaikan' },
                      { idx: 4, label: '5. Kepemimpinan & Sosial' },
                      { idx: 5, label: '6. Kreativitas & Ekstra' },
                      { idx: 6, label: '7. Kemandirian & Tugas' },
                      { idx: 7, label: '8. Religius & Adab' },
                      { idx: 8, label: '9. Kebugaran Jasmani' },
                      { idx: 9, label: '10. Reflektif Masa Depan' },
                    ].map((item) => {
                      const isActive = (variationMap[selectedStudent.id] || 0) === item.idx;
                      return (
                        <button
                          key={item.idx}
                          type="button"
                          onClick={() => {
                            const updatedVars = { ...variationMap, [selectedStudent.id]: item.idx };
                            const newSentence = generateSentenceVariations(selectedStudent, item.idx);
                            const updatedNotes = { ...notesMap, [selectedStudent.id]: newSentence };
                            setVariationMap(updatedVars);
                            setNotesMap(updatedNotes);
                            saveToStorage(updatedNotes, updatedVars);
                          }}
                          className={`px-2.5 py-1 rounded-lg text-[10px] font-extrabold transition-all cursor-pointer ${
                            isActive
                              ? 'bg-emerald-600 text-white shadow-2xs'
                              : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-100 border border-slate-200 dark:border-slate-700'
                          }`}
                        >
                          {item.label}
                        </button>
                      );
                    })}
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => handleCopyNote(selectedStudent.id, activeNoteSentence)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white dark:bg-slate-800 hover:bg-slate-100 text-slate-700 dark:text-slate-200 font-bold rounded-xl text-xs border border-slate-300 dark:border-slate-700 transition-all cursor-pointer shadow-2xs"
                    >
                      {copiedId === selectedStudent.id ? (
                        <>
                          <Check size={14} className="text-emerald-500" />
                          <span className="text-emerald-600 font-black">Tersalin!</span>
                        </>
                      ) : (
                        <>
                          <Copy size={14} />
                          <span>Salin Teks</span>
                        </>
                      )}
                    </button>

                    <button
                      type="button"
                      onClick={() => handleSaveNote(selectedStudent.id)}
                      className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl text-xs shadow-md transition-all cursor-pointer"
                    >
                      {savedSuccessId === selectedStudent.id ? (
                        <>
                          <CheckCircle2 size={14} />
                          <span>Tersimpan!</span>
                        </>
                      ) : (
                        <>
                          <Save size={14} />
                          <span>Simpan</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        /* MODE 2: CLASS WIDE REKAP TABLE */
        <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-5 shadow-sm space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 dark:border-slate-700 pb-3">
            <div>
              <h3 className="font-bold text-slate-800 dark:text-white text-base">
                Rekapitulasi Catatan Rapor Wali Kelas ({classStudents.length} Siswa)
              </h3>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Tinjau dan salin catatan rapor seluruh siswa perwalian Kelas {homeroomClass} sekaligus.
              </p>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={handleGenerateAll}
                className="inline-flex items-center gap-1.5 px-3 py-2 bg-amber-400 hover:bg-amber-300 text-slate-950 font-black rounded-xl text-xs shadow-xs transition-all cursor-pointer"
              >
                <Zap size={14} />
                <span>Reset / Re-Generate All</span>
              </button>

              <button
                onClick={handlePrintReport}
                className="inline-flex items-center gap-1.5 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl text-xs shadow-xs transition-all cursor-pointer"
              >
                <Printer size={14} />
                <span>Cetak Rekap</span>
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 dark:bg-slate-900/80 text-[11px] font-black uppercase tracking-wider text-slate-500 dark:text-slate-400 border-b border-slate-200 dark:border-slate-700">
                  <th className="py-3 px-3 text-center w-12">No</th>
                  <th className="py-3 px-3 w-44">Siswa</th>
                  <th className="py-3 px-3 text-center w-24">Jml Nilai</th>
                  <th className="py-3 px-3 text-center w-24">Rata Rapor</th>
                  <th className="py-3 px-3 text-center w-28">Absensi (S/I/A)</th>
                  <th className="py-3 px-3">Narasi Catatan Wali Kelas di Rapor</th>
                  <th className="py-3 px-3 text-center w-28">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-xs">
                {classStudents.map((student, idx) => {
                  const analytics = classAnalytics[student.id];
                  const rank = rankings[student.id];
                  const note = getStudentNoteSentence(student);

                  return (
                    <tr
                      key={student.id}
                      className="hover:bg-slate-50/80 dark:hover:bg-slate-700/40 transition-colors"
                    >
                      <td className="py-3 px-3 text-center font-bold text-slate-400">
                        {idx + 1}
                      </td>
                      <td className="py-3 px-3">
                        <span className="font-bold text-slate-800 dark:text-white block">
                          {student.name}
                        </span>
                        <span className="text-[10px] text-slate-500">
                          NIS: {student.nis || '-'} • Rangking #{rank?.rank || '-'}
                        </span>
                      </td>
                      <td className="py-3 px-3 text-center font-black text-indigo-600 dark:text-indigo-400">
                        {analytics?.totalGradeSum || '-'}
                      </td>
                      <td className="py-3 px-3 text-center font-black text-emerald-600 dark:text-emerald-400">
                        {analytics?.overallAvg || '-'}
                      </td>
                      <td className="py-3 px-3 text-center text-[11px]">
                        <span className="text-amber-600 font-bold">{analytics?.sakit || 0}S</span>{' '}
                        <span className="text-blue-600 font-bold">{analytics?.izin || 0}I</span>{' '}
                        <span className="text-rose-600 font-bold">{analytics?.alpa || 0}A</span>
                      </td>
                      <td className="py-3 px-3">
                        <textarea
                          rows={3}
                          value={notesMap[student.id] !== undefined ? notesMap[student.id] : note}
                          onChange={(e) => handleNoteChange(student.id, e.target.value)}
                          className="w-full p-2 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-xs leading-relaxed text-slate-800 dark:text-slate-200 outline-none focus:ring-1 focus:ring-emerald-500"
                        />
                      </td>
                      <td className="py-3 px-3 text-center space-y-1">
                        <button
                          type="button"
                          onClick={() => handleRotateVariation(student.id)}
                          className="w-full px-2 py-1 bg-amber-500 hover:bg-amber-600 text-slate-950 text-[10px] font-black rounded-lg transition-all flex items-center justify-center gap-1 cursor-pointer"
                          title="Ganti variasi kalimat"
                        >
                          <RefreshCw size={11} />
                          <span>Rubah</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => handleCopyNote(student.id, note)}
                          className="w-full px-2 py-1 bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-800 dark:text-slate-200 text-[10px] font-bold rounded-lg transition-all flex items-center justify-center gap-1 cursor-pointer"
                        >
                          {copiedId === student.id ? (
                            <span className="text-emerald-600 dark:text-emerald-400 font-black">
                              Copied!
                            </span>
                          ) : (
                            <>
                              <Copy size={11} />
                              <span>Salin</span>
                            </>
                          )}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
